import { Component, ViewChild, OnInit, Inject, HostListener, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SignupService, AlertService, AccountService } from '@app/_services';
import { EmptyStringValidator, MustMatch } from '@app/_helpers';
import { FadvIconLibraryService } from '@fa-switch-sharedservices/ng-elements';
import { CreateUserRequest, Account, UpdateUserRequest } from '@app/_models';
import { breakpointPixels, FadvModalComponent, FadvSwitchComponent, FadvToastComponent } from '@fa-switch-sharedservices/ng-elements';


@Component({
  selector: 'app-user-modal',
  templateUrl: './user-modal.component.html',
  styleUrls: ['./user-modal.component.less']
})
export class UserModalComponent implements OnInit {
  form: UntypedFormGroup;
  loading = false;
  submitted = false
  submitDisabled = true;
  clientID: number;
  firstNameInvalid: boolean;
  lastNameInvalid: boolean;
  phoneNumberInvalid: boolean;
  emailInvalid: boolean;
  confirmEmailInvalid: boolean;
  cancelAlignment: string;
  confirmAlignment: string;
  innerWidth: number = window.innerWidth
  displayFirstName: string;
  displayLastName: string;
  firstNameVariant: string;
  lastNameVariant: string;
  createUserRequest: CreateUserRequest;
  updateUserRequest: UpdateUserRequest;
  isCreateUser: boolean;
  modalHeader: string;
  userToUpdate: Account;
  userAlreadyExists: string = "User already exists on another account. Contact customer service."
  userAlreadyExistsBool = false;
  userStatus: string;

  @ViewChild('statusSwitch') statusSwitch!: FadvSwitchComponent;
  @ViewChild('userModal') userModal!: FadvModalComponent;
  @ViewChild('toastChangeStatus') toastChangeStatus!: FadvToastComponent;
  @Output() successfulSubmission: EventEmitter<string> = new EventEmitter();
  @Output() cancelEntry: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('toastSubmitError') toastSubmitError!: FadvToastComponent;
  @ViewChild('toastNonExistError') toastNonExistError!: FadvToastComponent;
  
  constructor(
    private formBuilder: UntypedFormBuilder,
    private signupService: SignupService,
    private accountService: AccountService,
    private alertService: AlertService
  ) {
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      status: [false],
      firstName: ['', [Validators.required, Validators.pattern('[a-z A-Z  -\/:-@\[-\`{-~]+$'), EmptyStringValidator]],
      lastName: ['', [Validators.required, Validators.pattern('[a-z A-Z  -\/:-@\[-\`{-~]+$'), EmptyStringValidator]],
      email: ['', [Validators.required, Validators.email]],
      confirmEmail: ['', [Validators.required, MustMatch]],
      phoneNumber: ['', [Validators.required, Validators.minLength(10), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]],
    },
      {
        validator: MustMatch('email', 'confirmEmail')
      });

    this.handleViewChange();

    this.displayFirstName = "First";
    this.displayLastName = "Last";
    this.firstNameVariant = "neutral";
    this.lastNameVariant = "neutral";

    this.signupService.getClientSMB().subscribe(response => {
      this.clientID = response['clientID'];
    });
  }

  ngAfterViewInit(): void{
    if(this.isCreateUser == false){
      if(this.userToUpdate.email == this.accountService.accountValue.email || this.userToUpdate.role == 'Admin'){
        this.statusSwitch.disabled = true;
      }
      else{
        this.statusSwitch.disabled = false;
        this.statusSwitch.checked = this.userToUpdate.isActive;
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerWidth = window.innerWidth
    this.handleViewChange();
  }

  handleViewChange(): void {
    if (this.innerWidth <= breakpointPixels.md) {
      this.cancelAlignment = "center"
      this.confirmAlignment = "center"
    } else {
      this.cancelAlignment = "right";
      this.confirmAlignment = "left";
    }
  }

  showValidation(field: string) {
    if (this.form.controls[field].invalid) {
      this[field + "Invalid"] = true;
    }
    else {
      this[field + "Invalid"] = false;
    }
  }

  showNew() {
    this.form.reset();
    this.isCreateUser = true;
    this.statusSwitch.disabled = true;
    this.statusSwitch.checked = true;
    this.createUserRequest = new CreateUserRequest();
    this.modalHeader = "Add a New User"
    this.displayFirstName = "First";
    this.displayLastName = "Last";
    this.firstNameVariant = "neutral";
    this.lastNameVariant = "neutral";
    this.isCreateUser = true;
    this.userModal.show();
  }

  showUpdate(account: Account) {
    this.form.reset();
    this.isCreateUser = false;
    this.updateUserRequest = new UpdateUserRequest();
    this.userToUpdate = account;
    this.modalHeader = "Update User"
    this.displayFirstName = account.firstName;
    this.displayLastName = account.lastName;
    this.firstNameVariant = "";
    this.lastNameVariant = "";

    if(this.userToUpdate.email == this.accountService.accountValue.email || this.userToUpdate.role == 'Admin'){
      this.statusSwitch.disabled = true;
      this.statusSwitch.checked = this.userToUpdate.isActive;
    }
    else{
      this.statusSwitch.disabled = false;
      this.statusSwitch.checked = this.userToUpdate.isActive;
    }

    this.form.patchValue({
      firstName: account.firstName,
      lastName: account.lastName,
      email: account.email,
      confirmEmail: account.email,
      phoneNumber: account.phone
    });

    this.isCreateUser = false;
    this.userModal.show();
  }

  hide() {
    this.form.reset();
    this.userModal.hide();
    this.displayFirstName = "First";
    this.displayLastName = "Last";
    this.firstNameVariant = "neutral";
    this.lastNameVariant = "neutral";
    this.cancelEntry.emit(true);
    if(this.isCreateUser == false){
      this.statusSwitch.checked = false;
    }
    this.toastSubmitError.hide();
    this.toastChangeStatus.hide();
  }

  changeStatus(){
    if(this.statusSwitch.disabled == false){
      this.toastChangeStatus.show();
      this.userToUpdate.isActive = !this.userToUpdate.isActive;

      if(this.statusSwitch.checked == true){
        this.userStatus = 'Active'
      }
    
      if(this.statusSwitch.checked == false){
        this.userStatus = 'Inactive'
      }
    }
  }

  onSubmit() {
    if (this.form.invalid) {
      this.alertService.error("Please ensure your provided information is correct")
      this.loading = false;
      return;
    }

    if (this.isCreateUser) {
      this.createUserRequest.ClientID = this.clientID;
      this.createUserRequest.Email = this.form.get('email').value;
      this.createUserRequest.FirstName = this.form.get('firstName').value;
      this.createUserRequest.LastName = this.form.get('lastName').value;
      this.createUserRequest.PhoneNumber = this.form.get('phoneNumber').value;
      this.createUserRequest.Role = 'User';
      this.createUserRequest.IsActive = true;
    }

    if(!this.isCreateUser){
      this.updateUserRequest.ClientID = this.clientID;
      this.updateUserRequest.Email = this.form.get('email').value;
      this.updateUserRequest.FirstName = this.form.get('firstName').value;
      this.updateUserRequest.LastName = this.form.get('lastName').value;
      this.updateUserRequest.Phone = this.form.get('phoneNumber').value;
      this.updateUserRequest.IsSMBUserUpdate = true;
      if(this.userToUpdate.role == "Admin"){
        this.updateUserRequest.Role = "Admin"
      }
      else{
        this.updateUserRequest.Role = 'User';
      }

      this.updateUserRequest.IsActive = this.statusSwitch.checked;

      if(this.userToUpdate.email == this.accountService.accountValue.email || this.userToUpdate.role == "Admin"){
        this.updateUserRequest.IsActive == true;
      }
    }
    

    if (this.isCreateUser == true) {
      this.accountService.userCreate(this.createUserRequest.FirstName, this.createUserRequest.LastName, this.createUserRequest.Email, this.createUserRequest.PhoneNumber, this.createUserRequest.Role, this.createUserRequest.ClientID, this.createUserRequest.IsActive).subscribe(response => {
        if (response.error == true) {
          if(response.message != "Already Exists"){
            this.toastNonExistError.message = response.message;
            this.toastNonExistError.show();
          }
          else{
            this.toastSubmitError.message = this.userAlreadyExists;
            this.toastSubmitError.show();
          }
        }
        else{
          this.handleSuccessfulSubmission(response.message);
          this.toastSubmitError.hide();
          this.userModal.hide();
        }
      })
    }

    if (this.isCreateUser == false) {
      this.accountService.update(this.userToUpdate.id, this.updateUserRequest).subscribe(response => {
        if (response.error == true) {
          this.toastSubmitError.message = response.message;
          this.toastSubmitError.show();
        }
        else{
          this.handleSuccessfulSubmission(response.message);
          this.toastChangeStatus.hide();
          this.userModal.hide();
        }
      })
    }

  }

  handleSuccessfulSubmission(response: string){
    this.successfulSubmission.emit(response)
  }

  updateFirstName() {
    if (!this.form.controls.firstName.invalid) {
      this.firstNameVariant = "";
      this.displayFirstName = this.form.get('firstName').value;
    }
    if (this.form.controls.firstName.invalid) {
      this.firstNameVariant = "neutral";
      this.displayFirstName = "First";
    }
  }

  updateLastName() {
    if (!this.form.controls.lastName.invalid) {
      this.lastNameVariant = "";
      this.displayLastName = this.form.get('lastName').value;
    }
    if (this.form.controls.lastName.invalid) {
      this.lastNameVariant = "neutral";
      this.displayLastName = "Last";
    }
  }
}
